import type { IntlShape } from "react-intl";
import { ZodIssueCode, type ZodObject, type ZodTypeAny, z } from "zod";
import { getMaxBasedOnUnit, parseNumber } from "../../../utils";
import type InputType from "../../Input/InputType";
import { JsonInputType } from "../../Input/JsonInputType";
import { SideName } from "../../Side/SideName";
import Plate from "../Plate";

export default class Gem extends Plate {
	public static code = "plate_gem";

	public getDimensionsString(input: InputType, intl: IntlShape) {
		const { length_1, length_2, width_1, width_2 } = this.formatInputs(input, intl, "decimal");
		if (length_1 && length_2 && width_1 && width_2) {
			return `(${length_1} + ${length_2}) x (${width_1}, ${width_2}) cm`;
		}

		return undefined;
	}

	public calculateSideLength(side: SideName, input: InputType) {
		const length1 = Number(input[JsonInputType.LengthSection1]);
		const length2 = Number(input[JsonInputType.LengthSection2]);
		const width1 = Number(input[JsonInputType.WidthSection1]);
		const width2 = Number(input[JsonInputType.WidthSection2]);

		if (
			!(
				Number.isNaN(length1) ||
				Number.isNaN(length2) ||
				Number.isNaN(width1) ||
				Number.isNaN(width2)
			)
		) {
			switch (side) {
				case SideName.Length1: {
					const w = Math.max(width1, width2);
					const b = Math.min(width1, width2);
					const t1l = Math.sqrt(((w - b) / 2) ** 2 + length1 ** 2);
					const t2l = Math.sqrt((w / 2) ** 2 + length2 ** 2);
					return b + 2 * (t1l + t2l);
				}
			}
		}

		return undefined;
	}

	public calculateSheetDimensions(input: InputType): { length: number; width: number } | undefined {
		const length1 = Number(input[JsonInputType.LengthSection1]);
		const length2 = Number(input[JsonInputType.LengthSection2]);
		const width1 = Number(input[JsonInputType.WidthSection1]);
		const width2 = Number(input[JsonInputType.WidthSection2]);

		if (
			!(
				Number.isNaN(length1) ||
				Number.isNaN(length2) ||
				Number.isNaN(width1) ||
				Number.isNaN(width2)
			)
		) {
			return { length: length1 + length2, width: Math.max(width1, width2) };
		}
	}

	protected getDimensionSchema(): ZodTypeAny {
		const inputs = super.getDimensionSchema() as ZodObject<Record<string, ZodTypeAny>>;
		const length1Input = this.getInputById(JsonInputType.LengthSection1);
		const length2Input = this.getInputById(JsonInputType.LengthSection2);

		if (!(length1Input && length2Input)) {
			return inputs;
		}

		const length1Max = getMaxBasedOnUnit(length1Input.max, this.unit);
		const length2Max = getMaxBasedOnUnit(length2Input.max, this.unit);

		return inputs
			.extend({
				length_1: z.preprocess(parseNumber, z.number().min(1).max(length1Max)),
				length_2: z.preprocess(parseNumber, z.number().min(1).max(length2Max)),
			})
			.superRefine((data, context) => {
				const width1Input =
					this.getInputById(JsonInputType.WidthSection1) ?? this.shapeInput.inputs[0];
				const width2Input = this.getInputById(JsonInputType.WidthSection2);
				const minWidth = Number(width1Input?.min);

				const minWidthLength2Input = length2Input.min;
				const maxWidthlength2Input = getMaxBasedOnUnit(length2Input.max, this.unit);
				const totalWidth = Number(data.length_1) + Number(data.length_2);

				if (totalWidth < minWidthLength2Input) {
					const issue = {
						code: ZodIssueCode.custom,
						params: {
							type: "combined_too_small",
							field_1: length1Input?.label,
							field_2: length2Input.label,
							minValue: minWidthLength2Input,
							totalValue: totalWidth,
						},
					};
					context.addIssue({ ...issue, path: [JsonInputType.LengthSection1] });
					context.addIssue({ ...issue, path: [JsonInputType.LengthSection2] });
				}

				if (totalWidth > maxWidthlength2Input) {
					const issue = {
						code: ZodIssueCode.custom,
						params: {
							type: "combined_too_big",
							field_1: length1Input?.label,
							field_2: length2Input.label,
							maxValue: maxWidthlength2Input,
							totalValue: totalWidth,
						},
					};
					context.addIssue({ ...issue, path: [JsonInputType.LengthSection1] });
					context.addIssue({ ...issue, path: [JsonInputType.LengthSection2] });
				}

				if (
					data.width_2 >= data.width_1 &&
					Number(data.length_1) < minWidth &&
					Number(data.length_2) < minWidth
				) {
					const issue = {
						code: ZodIssueCode.custom,
						params: {
							type: "two_too_small",
							field_1: this.shapeInput.inputs.find((input) => input.id === "length_1")?.label,
							field_2: this.shapeInput.inputs.find((input) => input.id === "length_2")?.label,
							minValue: minWidth,
						},
					};
					context.addIssue({ ...issue, path: ["length_1"] });
					context.addIssue({ ...issue, path: ["length_2"] });
				}

				if (data.width_2 < data.width_1 && data.width_2 >= (width2Input?.min ?? 0)) {
					context.addIssue({
						code: z.ZodIssueCode.custom,
						params: {
							type: "difference_two_fields_minimum",
							field_1: [this.getInputById(JsonInputType.WidthSection2)?.label],
							field_2: [this.getInputById(JsonInputType.WidthSection1)?.label],
						},
						path: ["width_2"],
					});
				}
			});
	}

	protected calculateMaxBeltSize(data: Record<JsonInputType, number>) {
		return super.calculateMaxBeltSize({
			[JsonInputType.Length]: data.length_1 + data.length_2,
			[JsonInputType.Width]: Math.max(data.width_1, data.width_2),
		} as Record<JsonInputType, number>);
	}
}
